import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Headphones, Map, BedDouble, Car, BadgeIndianRupee, Cloud } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import GoogleReviews from "../components/GoogleReviews";
import InstagramSection from "../components/InstagramSection";
import { useSite, resolveImg } from "../context/SiteContext";
import ItineraryForm from "../components/ItineraryForm";

const ICONS = { Compass, Headphones, Map, BedDouble, Car, BadgeIndianRupee };

export default function Home() {
  const { brand, states, packages, faqs, why, gallery, heroImage } = useSite();
  const featuredPkgs = (packages.some((p) => p.featured) ? packages.filter((p) => p.featured) : packages).slice(0, 3);
  return (
    <main data-testid="home-page" className="bg-background text-foreground">
      {/* HERO */}
      <section className="relative h-screen min-h-[680px] w-full overflow-hidden">
        <img
          src={resolveImg(heroImage)}
          alt="Meghalaya waterfall"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

        <motion.div
          className="cloud absolute top-24 left-[10%] h-20 w-40 rounded-full bg-white/30 blur-2xl"
          animate={{ x: [0, 40, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="cloud absolute top-40 right-[8%] h-28 w-52 rounded-full bg-white/20 blur-3xl"
          animate={{ x: [0, -50, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
        <Cloud className="absolute top-32 left-[20%] h-10 w-10 text-white/60 cloud" />
        <Cloud className="absolute top-44 right-[24%] h-14 w-14 text-white/40 cloud" style={{ animationDelay: "3s" }} />

        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-5 text-white/90">
              <span className="h-px w-10 bg-white/70" />
              <span className="text-[11px] uppercase tracking-[0.32em]">North East India · by {brand.short}</span>
            </div>
            <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] text-balance">
              Explore the Hidden<br />
              <em className="not-italic font-medium">Paradise</em> of North East India
            </h1>
            <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl">
              Custom Tours · Honeymoon Packages · Family Trips · Adventure · Local Experiences
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#itinerary">
                <Button data-testid="hero-plan-trip" size="lg" className="rounded-full bg-white text-foreground hover:bg-white/90 px-8 h-12">
                  Plan My Trip <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/packages">
                <Button data-testid="hero-view-packages" size="lg" variant="outline" className="rounded-full border-white/60 text-white bg-white/10 backdrop-blur-md hover:bg-white/20 px-8 h-12">
                  View Packages
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase">
          Scroll
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-muted/30 py-5 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap text-foreground/60 font-display text-xl">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex gap-12 px-6">
              {["Living Root Bridges", "Tawang Monastery", "Crystal Dawki", "Kaziranga Rhinos", "Ziro Valley", "Sela Pass", "Mawlynnong"].map((t) => (
                <span key={t} className="flex items-center gap-12">
                  <span>{t}</span>
                  <span className="text-primary">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* DESTINATIONS BENTO */}
      <section id="destinations" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Destinations</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-balance max-w-2xl">
              Three states. <em className="not-italic font-medium">One unforgettable</em> arc.
            </h2>
            <p className="text-muted-foreground max-w-md">
              The Eastern Himalayas hold some of India&apos;s last hidden valleys, rivers and tribes. Begin here.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {states.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12"}>
              <Link to={`/destinations?state=${s.slug === "arunachal" ? "Arunachal Pradesh" : s.name}`} data-testid={`dest-card-${s.slug}`} className="group block relative overflow-hidden rounded-3xl">
                <div className={`relative ${i === 2 ? "h-[44vh]" : "h-[56vh]"} w-full`}>
                  <img src={resolveImg(s.cover)} alt={s.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end text-white">
                    <div className="text-[11px] uppercase tracking-[0.32em] text-white/70 mb-2">{s.tagline}</div>
                    <div className="flex items-end justify-between gap-6">
                      <h3 className="font-display text-3xl md:text-5xl font-light">{s.name}</h3>
                      <ArrowRight className="h-6 w-6 translate-x-0 group-hover:translate-x-2 transition-transform" />
                    </div>
                    <p className="mt-3 text-white/80 max-w-md text-sm md:text-base">{s.blurb}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PACKAGES PREVIEW */}
      <section id="packages-preview" className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Tour Packages</SectionLabel>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <h2 className="font-display text-4xl md:text-5xl font-light text-balance max-w-2xl">
                Five curated journeys. <em className="not-italic font-medium">All-inclusive</em>, never templated.
              </h2>
              <Link to="/packages" className="text-primary text-sm flex items-center gap-2" data-testid="see-all-packages">
                See all packages <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredPkgs.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="group rounded-3xl overflow-hidden border border-border bg-card hover:shadow-2xl transition-shadow duration-500">
                  <div className="relative h-64 overflow-hidden">
                    <img src={resolveImg(p.cover)} alt={p.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]" />
                    <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-xs tracking-wide">{p.days}D · {p.nights}N</div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl font-medium mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{p.places.join(" · ")}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <div className="text-xs text-muted-foreground">Starting from</div>
                        <div className="font-display text-xl">₹{p.price.toLocaleString("en-IN")}</div>
                      </div>
                      <Link to={`/booking/${p.id}`}>
                        <Button data-testid={`book-${p.id}`} className="rounded-full" size="sm">Book Now</Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TRAVEL WITH RANDZ */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Why travel with us</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-light text-balance max-w-3xl mb-16">
            Why Travel With <em className="not-italic font-medium">RandZ</em>
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {why.map((item, i) => {
            const Icon = ICONS[item.icon] || Compass;
            return (
              <Reveal key={item.title} delay={i * 0.06}>
                <div className="rounded-2xl border border-border p-7 h-full hover:border-primary hover:-translate-y-1 transition-all duration-500 bg-card">
                  <div className="h-12 w-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="font-display text-xl mb-1">{item.title}</div>
                  <div className="text-sm text-muted-foreground">{item.desc}</div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CUSTOM ITINERARY */}
      <section id="itinerary" className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <div className="text-[11px] uppercase tracking-[0.32em] text-primary-foreground/70 mb-4">Bespoke</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-balance">
              Design Your <em className="not-italic font-medium">Dream Trip</em>
            </h2>
            <p className="mt-6 text-primary-foreground/80 max-w-md leading-relaxed">
              Tell us a little about how you travel — we&apos;ll send back a fully crafted itinerary within 24 hours, with stays, drives and reading lists.
            </p>
            <ul className="mt-8 space-y-3 text-primary-foreground/85">
              <li>· Hand-built around your dates and pace</li>
              <li>· Trusted accommodation partners</li>
              <li>· Permits, transfers, guides — all handled</li>
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-6 md:p-8 text-foreground">
              <ItineraryForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>Gallery</SectionLabel>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-light max-w-2xl">Stories told in <em className="not-italic font-medium">light</em>{"."}</h2>
            <Link to="/gallery" className="text-primary text-sm flex items-center gap-2" data-testid="see-all-gallery">Open gallery <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </Reveal>
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {gallery.slice(0, 8).map((g, i) => (
            <Reveal key={g.id || g.url} delay={i * 0.04}>
              <div className="overflow-hidden rounded-2xl group break-inside-avoid">
                <img src={resolveImg(g.url)} alt={g.caption || "North East gallery"} className="w-full transition-transform duration-[1200ms] group-hover:scale-110" loading="lazy" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GOOGLE REVIEWS (replaces fake testimonials) */}
      <GoogleReviews />

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* FAQ */}
      <section className="py-24 md:py-32 max-w-4xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl font-light mb-12">Questions, answered.</h2>
        </Reveal>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-display text-lg">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  );
}
